import { inject, Injectable } from '@angular/core';
import { DistanceUnit, LengthUnit, UnitSystemType, WeightUnit } from '@data-access';
import { AnyUnit, convertUnit, UnitCategory, UnitDisplay } from '../utils/unit-conversion.utils';
import { CoreService } from '@core-session';

@Injectable({ providedIn: 'root' })
export class UnitSystemService {
  private coreService = inject(CoreService);

  private resolveUnit(category: UnitCategory, unitSystem?: UnitSystemType | null): string {
    const system = unitSystem ?? this.coreService.getUnitSystem() ?? UnitSystemType.Imperial;
    const isMetric = system === UnitSystemType.Metric;

    switch (category) {
      case UnitCategory.Distance:
        return isMetric ? DistanceUnit.Km : DistanceUnit.Mi;
      case UnitCategory.Length:
        return isMetric ? LengthUnit.Cm : LengthUnit.In;
      case UnitCategory.Weight:
        return isMetric ? WeightUnit.Kg : WeightUnit.Lb;
    }
  }

  resolveValue(
    unitsMap: Record<string, number> | null | undefined,
    category: UnitCategory,
    unitSystem?: UnitSystemType | null,
  ): number | null {
    if (!unitsMap) {
      return null;
    }
    return unitsMap[this.resolveUnit(category, unitSystem)] ?? null;
  }

  convertValueFromBase(
    value: number | null | undefined,
    category: UnitCategory,
    unitSystem: UnitSystemType | null = null,
  ): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const base = this.resolveUnit(category, UnitSystemType.Imperial) as AnyUnit;
    const target = this.resolveUnit(category, unitSystem) as AnyUnit;
    return convertUnit(value, base, target);
  }

  resolveLabelKey(
    category: UnitCategory,
    unitSystem?: UnitSystemType | null,
    display: UnitDisplay = 'short',
  ): string {
    return `common.units.${display}.${this.resolveUnit(category, unitSystem)}`;
  }

  resolveUnitValue(
    data: Record<WeightUnit, number> | Record<LengthUnit, number>,
    unitSystem?: UnitSystemType | null,
  ) {
    const keys = Object.keys(data);
    if (keys.length === 0) return 0;

    const firstKey = keys[0];
    let category: UnitCategory;

    if (Object.values(WeightUnit).includes(firstKey as WeightUnit)) {
      category = 'WEIGHT';
    } else if (Object.values(LengthUnit).includes(firstKey as LengthUnit)) {
      category = 'LENGTH';
    } else {
      return 0;
    }

    return (data as Record<string, number>)?.[this.resolveUnit(category, unitSystem)] ?? 0;
  }
}
